/**
 * Failure scenarios (constants.js FAILURE_SCENARIOS) and the controller that
 * steps them (failure-simulator.js), ported without the DOM. Timing is in
 * simulation updates, not seconds: a scenario completes after maxUpdates
 * (default 8, settable 6..20) cycles, with ceil(maxUpdates / stages) updates
 * per stage. Stage 0 applies the moment a scenario starts; later stages
 * advance from onCycleComplete(), which simulation.ts calls once per batch.
 * That is the fix for the audited bug: the original counted
 * livedata:cycleComplete, which only the main-thread fallback emitted, so in
 * worker mode a scenario never left stage 0.
 */
import type { FailureEffectsMap, FailureStageEffects } from './generator';

export type FailureAlertLevel = 'INFO' | 'WARNING' | 'CRITICAL';

export interface FailureStage extends FailureStageEffects {
  /** Original wall-clock duration, informational only. */
  duration: number;
  alert: FailureAlertLevel;
  message: string;
  /** MODEM_UNLOCK only; the generator applies numeric effects, not this label. */
  status?: string;
}

export interface FailureScenario {
  id: string;
  name: string;
  description: string;
  affectedSensors: string[];
  totalDuration: number;
  stages: FailureStage[];
}

export const FAILURE_SCENARIOS: Record<string, FailureScenario> = {
  FAN_FAILURE: {
    id: 'FAN_FAILURE',
    name: 'Cooling Fan Failure',
    description: 'Simulates gradual fan degradation leading to overheating',
    affectedSensors: ['fan_speed', 'fan_temperature', 'lna_temperature', 'hpa_temperature'],
    totalDuration: 1200000,
    stages: [
      { duration: 300000, fanSpeedDrop: 0.1, tempRise: 0, alert: 'INFO', message: 'Fan performance slightly reduced' },
      { duration: 300000, fanSpeedDrop: 0.3, tempRise: 5, alert: 'WARNING', message: 'Fan speed dropping - monitor temperature' },
      { duration: 300000, fanSpeedDrop: 0.6, tempRise: 15, alert: 'WARNING', message: 'Significant fan degradation detected' },
      { duration: 300000, fanSpeedDrop: 0.9, tempRise: 30, alert: 'CRITICAL', message: 'Fan failure imminent - immediate action required' },
    ],
  },
  LNA_DEGRADATION: {
    id: 'LNA_DEGRADATION',
    name: 'LNA Degradation',
    description: 'Simulates Low Noise Amplifier performance degradation',
    affectedSensors: ['lna_noise_figure', 'lna_temperature', 'lna_gain'],
    totalDuration: 1800000,
    stages: [
      { duration: 600000, noiseFigureRise: 0.3, tempRise: 2, gainDrop: 1, alert: 'INFO', message: 'LNA performance slightly degraded' },
      { duration: 600000, noiseFigureRise: 0.8, tempRise: 5, gainDrop: 3, alert: 'WARNING', message: 'LNA noise figure increasing - signal quality affected' },
      { duration: 600000, noiseFigureRise: 1.5, tempRise: 10, gainDrop: 5, alert: 'CRITICAL', message: 'LNA critically degraded - backup switchover recommended' },
    ],
  },
  HPA_OVERTEMP: {
    id: 'HPA_OVERTEMP',
    name: 'HPA Overtemperature',
    description: 'Simulates High Power Amplifier thermal runaway',
    affectedSensors: ['hpa_temperature', 'hpa_output_power', 'hpa_efficiency'],
    totalDuration: 540000,
    stages: [
      { duration: 180000, tempRise: 10, powerDrop: 0, efficiencyDrop: 2, alert: 'WARNING', message: 'HPA temperature rising' },
      { duration: 180000, tempRise: 20, powerDrop: 0.1, efficiencyDrop: 5, alert: 'WARNING', message: 'HPA thermal limit approaching - power reduction initiated' },
      { duration: 180000, tempRise: 35, powerDrop: 0.3, efficiencyDrop: 10, alert: 'CRITICAL', message: 'HPA thermal protection active - significant power reduction' },
    ],
  },
  MOTOR_BEARING_WEAR: {
    id: 'MOTOR_BEARING_WEAR',
    name: 'Motor Bearing Wear',
    description: 'Simulates antenna motor bearing degradation',
    affectedSensors: ['antenna_az_current', 'antenna_el_current', 'antenna_vibration'],
    totalDuration: 1800000,
    stages: [
      { duration: 600000, currentRise: 0.2, vibrationRise: 1.0, alert: 'INFO', message: 'Motor current slightly elevated' },
      { duration: 600000, currentRise: 0.5, vibrationRise: 2.5, alert: 'WARNING', message: 'Bearing wear detected - schedule inspection' },
      { duration: 600000, currentRise: 1.0, vibrationRise: 5.0, alert: 'CRITICAL', message: 'Bearing failure imminent - maintenance required' },
    ],
  },
  MODEM_UNLOCK: {
    id: 'MODEM_UNLOCK',
    name: 'Modem Signal Unlock',
    description: 'Simulates modem losing signal lock',
    affectedSensors: ['modem_lock', 'modem_ber', 'modem_eb_n0'],
    totalDuration: 240000,
    stages: [
      { duration: 60000, status: 'ACQUIRING', berSpike: 1e-4, ebN0Drop: 3, alert: 'WARNING', message: 'Modem signal acquiring - data integrity check' },
      { duration: 60000, status: 'UNLOCKED', berSpike: 1e-2, ebN0Drop: 6, alert: 'CRITICAL', message: 'Modem lock lost - data reception interrupted' },
      { duration: 120000, status: 'ACQUIRING', berSpike: 1e-4, ebN0Drop: 3, alert: 'WARNING', message: 'Modem re-acquiring signal' },
    ],
  },
};

export const FAILURE_SCENARIO_IDS = Object.keys(FAILURE_SCENARIOS);

/** Equipment names for the stage alerts (failure-simulator.js getAffectedEquipment). */
export const AFFECTED_EQUIPMENT: Record<string, string> = {
  FAN_FAILURE: 'Cooling Fan',
  LNA_DEGRADATION: 'Low Noise Amplifier',
  HPA_OVERTEMP: 'High Power Amplifier',
  MOTOR_BEARING_WEAR: 'Antenna Motor',
  MODEM_UNLOCK: 'Satellite Modem',
};

export const DEFAULT_MAX_UPDATES = 8;
export const MIN_MAX_UPDATES = 6;
export const MAX_MAX_UPDATES = 20;

export function updatesPerStage(maxUpdates: number, stageCount: number): number {
  return Math.ceil(maxUpdates / stageCount);
}

/** Stage index after `updateCount` completed cycles. */
export function stageIndexFor(updateCount: number, maxUpdates: number, stageCount: number): number {
  return Math.min(Math.floor(updateCount / updatesPerStage(maxUpdates, stageCount)), stageCount - 1);
}

/** The effects map the generator consumes, for a set of scenarios at given stages (apply helper). */
export function effectsFor(active: Array<{ scenarioId: string; stageIndex: number }>): FailureEffectsMap {
  const map: FailureEffectsMap = {};
  for (const { scenarioId, stageIndex } of active) {
    const scenario = FAILURE_SCENARIOS[scenarioId];
    if (!scenario) throw new Error(`unknown failure scenario ${scenarioId}`);
    const stage = scenario.stages[Math.max(0, Math.min(stageIndex, scenario.stages.length - 1))];
    map[scenarioId] = stage;
  }
  return map;
}

/** Removes scenarios from an effects map (clear helper); with no ids, clears everything. */
export function withoutEffects(effects: FailureEffectsMap, scenarioIds?: string[]): FailureEffectsMap {
  if (!scenarioIds) return {};
  const next: FailureEffectsMap = { ...effects };
  for (const id of scenarioIds) delete next[id];
  return next;
}

export interface ActiveFailure {
  scenarioId: string;
  name: string;
  currentStage: number;
  totalStages: number;
  updateCount: number;
  maxUpdates: number;
  startTime: number;
  effects: FailureStage | null;
  completed: boolean;
}

export interface StageChange {
  scenarioId: string;
  stage: FailureStage;
  stageIndex: number;
  totalStages: number;
  updateCount: number;
}

export interface StageAlert {
  severity: FailureAlertLevel;
  title: string;
  message: string;
  scenarioId: string;
  equipment: string;
}

export interface FailureControllerEvents {
  onStarted?: (scenarioId: string, scenario: FailureScenario, maxUpdates: number) => void;
  onStageChanged?: (change: StageChange) => void;
  onStageAlert?: (alert: StageAlert) => void;
  onCompleted?: (info: { scenarioId: string; totalUpdates: number; totalDuration: number }) => void;
  onStopped?: (scenarioId: string) => void;
}

export interface FailureController {
  /** False when the id is unknown or already running. */
  start(scenarioId: string): boolean;
  stop(scenarioId: string): boolean;
  stopAll(): void;
  /** Call once per simulation cycle (batch); advances stages and completion. */
  onCycleComplete(): void;
  getActive(): ActiveFailure[];
  isActive(scenarioId: string): boolean;
  /** Effects per active scenario, the map handed to the generator. */
  currentEffects(): FailureEffectsMap;
  getMaxUpdates(): number;
  /** 6..20; false (and unchanged) otherwise. */
  setMaxUpdates(value: number): boolean;
}

interface FailureState {
  scenario: FailureScenario;
  currentStage: number;
  updateCount: number;
  startTime: number;
  effects: FailureStage | null;
  completed: boolean;
}

export function createFailureController(events: FailureControllerEvents = {}, options: { maxUpdates?: number; now?: () => number } = {}): FailureController {
  let maxUpdates = options.maxUpdates ?? DEFAULT_MAX_UPDATES;
  const now = options.now ?? (() => Date.now());
  const active = new Map<string, FailureState>();

  function executeStage(scenarioId: string, stageIndex: number): void {
    const failure = active.get(scenarioId);
    if (!failure) return;
    const scenario = failure.scenario;
    if (stageIndex >= scenario.stages.length) return;
    const stage = scenario.stages[stageIndex];
    failure.currentStage = stageIndex;
    failure.effects = stage;
    events.onStageChanged?.({ scenarioId, stage, stageIndex, totalStages: scenario.stages.length, updateCount: failure.updateCount });
    events.onStageAlert?.({
      severity: stage.alert || 'INFO',
      title: scenario.name,
      message: stage.message || `${scenario.name} - Stage ${stageIndex + 1} of ${scenario.stages.length}`,
      scenarioId,
      equipment: AFFECTED_EQUIPMENT[scenario.id] || scenario.name,
    });
  }

  return {
    start(scenarioId) {
      const scenario = FAILURE_SCENARIOS[scenarioId];
      if (!scenario) {
        console.warn('[failure] unknown scenario:', scenarioId);
        return false;
      }
      if (active.has(scenarioId)) return false;
      active.set(scenarioId, { scenario, currentStage: -1, updateCount: 0, startTime: now(), effects: null, completed: false });
      events.onStarted?.(scenarioId, scenario, maxUpdates);
      executeStage(scenarioId, 0);
      return true;
    },
    stop(scenarioId) {
      if (!active.delete(scenarioId)) return false;
      events.onStopped?.(scenarioId);
      return true;
    },
    stopAll() {
      for (const id of [...active.keys()]) this.stop(id);
    },
    onCycleComplete() {
      for (const [scenarioId, failure] of active) {
        if (failure.completed) continue;
        failure.updateCount += 1;
        const totalStages = failure.scenario.stages.length;
        const target = stageIndexFor(failure.updateCount, maxUpdates, totalStages);
        if (target > failure.currentStage) executeStage(scenarioId, target);
        if (failure.updateCount >= maxUpdates && !failure.completed) {
          failure.completed = true;
          events.onCompleted?.({ scenarioId, totalUpdates: failure.updateCount, totalDuration: now() - failure.startTime });
        }
      }
    },
    getActive() {
      return [...active.entries()].map(([scenarioId, f]) => ({
        scenarioId,
        name: f.scenario.name,
        currentStage: f.currentStage,
        totalStages: f.scenario.stages.length,
        updateCount: f.updateCount,
        maxUpdates,
        startTime: f.startTime,
        effects: f.effects,
        completed: f.completed,
      }));
    },
    isActive: (scenarioId) => active.has(scenarioId),
    currentEffects() {
      const map: FailureEffectsMap = {};
      for (const [scenarioId, f] of active) if (f.effects) map[scenarioId] = f.effects;
      return map;
    },
    getMaxUpdates: () => maxUpdates,
    setMaxUpdates(value) {
      const next = Math.trunc(value);
      if (Number.isNaN(next) || next < MIN_MAX_UPDATES || next > MAX_MAX_UPDATES) {
        console.warn(`[failure] invalid max updates ${value} (must be ${MIN_MAX_UPDATES}-${MAX_MAX_UPDATES})`);
        return false;
      }
      maxUpdates = next;
      return true;
    },
  };
}
